// Public risk-report intake page. Opened when a member of the public
// scans a site's QR sticker with their phone camera. The QR encodes
// `https://<host>/report/<orgSlug>/<siteId>`, so this page is
// reachable without a login, without a workspace context switch, and
// without any of the auth store hydration.
//
// Two states:
//   • form  — dropdown of risk categories + name + email + optional
//             note. Selecting a category expands the "escalate
//             immediately if" guidance from the backend catalogue.
//   • done  — confirmation card with the freshly-issued REQ-NNNNN
//             code so the reporter has something to reference if
//             they need to follow up.
//
// This intentionally avoids the app's Axios client + auth interceptor
// because those add JWT / customer scope / retry logic none of which
// fit an unauthenticated form. Plain `fetch` keeps it small and
// dependency-free.

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert, Box, Button, CircularProgress, Container, IconButton, MenuItem, Paper,
  Stack, TextField, Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { baseUrl } from '../../lib/api'

type RiskCategory = { key: string; label: string; escalateIf: string }

type BootPayload = {
  org: { slug: string; name: string }
  site: { id: string; name: string; address: string | null }
  team: { id: string; name: string } | null
  risk_categories: RiskCategory[]
}

type SubmitResponse = {
  id: string
  code: string
  status: string
  created_at: string
}

export function PublicReportPage() {
  const { orgSlug = '', siteId = '' } = useParams<{ orgSlug: string; siteId: string }>()

  // ── boot ──────────────────────────────────────────────────────
  const [boot, setBoot] = useState<BootPayload | null>(null)
  const [bootErr, setBootErr] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const res = await fetch(
          `${baseUrl}/api/v1/public/site-qr/${encodeURIComponent(orgSlug)}/${encodeURIComponent(siteId)}`,
        )
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.message ?? `HTTP ${res.status}`)
        }
        const data = (await res.json()) as BootPayload
        if (live) setBoot(data)
      } catch (e) {
        if (live) setBootErr(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { live = false }
  }, [orgSlug, siteId])

  // ── form ──────────────────────────────────────────────────────
  const [categoryKey, setCategoryKey] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState<string | null>(null)
  const [done, setDone] = useState<SubmitResponse | null>(null)

  // Image attachments — up to 4 photos. Each entry tracks its upload
  // lifecycle: `uploading` while the POST is in flight, then `url`
  // once the server confirms. Client rejects any file that isn't an
  // image or that pushes us over the cap.
  type Attachment = {
    id: string
    file: File
    previewUrl: string   // object URL for the local preview thumbnail
    url: string | null   // MinIO URL once uploaded
    error: string | null
  }
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const MAX_ATTACHMENTS = 4
  const MAX_BYTES = 5 * 1024 * 1024

  async function handleFilesPicked(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return
    const room = MAX_ATTACHMENTS - attachments.length
    if (room <= 0) return
    const picked = Array.from(fileList).slice(0, room)
    // Client-side filter — we ALSO validate on the server, this
    // just spares round-trips for obvious junk (PDFs, oversized).
    const staged: Attachment[] = picked.map((file) => {
      let err: string | null = null
      if (!file.type.startsWith('image/')) err = 'Not an image'
      else if (file.size > MAX_BYTES) err = 'Larger than 5 MB'
      return {
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        url: null,
        error: err,
      }
    })
    setAttachments((cur) => [...cur, ...staged])
    // Kick off each upload; the state update above is our source of
    // truth for the thumbnail row, and each promise below patches
    // the matching row when it lands (or errors out).
    for (const a of staged) {
      if (a.error) continue
      const form = new FormData()
      form.append('file', a.file, a.file.name)
      try {
        const res = await fetch(
          `${baseUrl}/api/v1/public/site-qr/${encodeURIComponent(orgSlug)}/${encodeURIComponent(siteId)}/upload-image`,
          { method: 'POST', body: form },
        )
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message ?? `HTTP ${res.status}`)
        setAttachments((cur) => cur.map((c) => c.id === a.id
          ? { ...c, url: data.url as string } : c))
      } catch (e) {
        setAttachments((cur) => cur.map((c) => c.id === a.id
          ? { ...c, error: e instanceof Error ? e.message : String(e) } : c))
      }
    }
  }

  function removeAttachment(id: string) {
    setAttachments((cur) => {
      const target = cur.find((a) => a.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return cur.filter((a) => a.id !== id)
    })
  }

  const selected = useMemo(
    () => boot?.risk_categories.find((c) => c.key === categoryKey) ?? null,
    [boot, categoryKey],
  )

  const anyUploading = attachments.some((a) => !a.url && !a.error)
  const uploadedImageUrls = attachments.map((a) => a.url).filter((u): u is string => !!u)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!categoryKey || !name.trim() || !email.trim()) return
    // Wait for any in-flight image uploads before submitting so the
    // report row lands with the full gallery, not a partial one.
    if (anyUploading) return
    setSubmitting(true)
    setSubmitErr(null)
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/public/site-qr/${encodeURIComponent(orgSlug)}/${encodeURIComponent(siteId)}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            risk_category:   categoryKey,
            submitter_name:  name.trim(),
            submitter_email: email.trim(),
            description:     description.trim() || undefined,
            image_urls:      uploadedImageUrls.length > 0 ? uploadedImageUrls : undefined,
          }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message ?? `HTTP ${res.status}`)
      setDone(data as SubmitResponse)
    } catch (e2) {
      setSubmitErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      setSubmitting(false)
    }
  }

  // ── render ────────────────────────────────────────────────────
  const bg = { minHeight: '100vh', bgcolor: '#F3F4F6', py: { xs: 3, sm: 6 } }

  if (bootErr) {
    return (
      <Box sx={bg}>
        <Container maxWidth="sm">
          <Alert severity="error" icon={<WarningAmberIcon />}>
            Couldn't load this site's report form: {bootErr}
          </Alert>
        </Container>
      </Box>
    )
  }

  if (!boot) {
    return (
      <Box sx={{ ...bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (done) {
    return (
      <Box sx={bg}>
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: 'center' }} elevation={0}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              Report submitted
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              Reference <strong>{done.code}</strong>. The {boot.team?.name ?? boot.org.name} team has been notified.
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
              You can safely close this page.
            </Typography>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={bg}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }} elevation={0}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'primary.main', mb: 0.5 }}>
            Report a risk
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            {boot.site.name}
          </Typography>
          {boot.site.address && (
            <Typography sx={{ color: 'text.secondary', mb: 3 }}>{boot.site.address}</Typography>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.25}>
              <TextField
                select label="Risk category" required fullWidth
                value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)}
                size="small"
              >
                <MenuItem value="" disabled>Select a category…</MenuItem>
                {boot.risk_categories.map((c) => (
                  <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                ))}
              </TextField>

              {selected && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ '& .MuiAlert-message': { fontSize: 13.5 } }}>
                  <strong>Escalate immediately if:</strong> {selected.escalateIf}
                </Alert>
              )}

              <TextField
                label="Your full name" required fullWidth size="small"
                value={name} onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
              <TextField
                label="Email" required fullWidth size="small"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <TextField
                label="Anything else we should know? (optional)"
                fullWidth size="small" multiline minRows={3}
                value={description} onChange={(e) => setDescription(e.target.value)}
              />

              {/* Image attachments — image-only picker + thumbnail
                  row. Each upload runs in parallel so a slow phone
                  camera doesn't block the whole form. */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Photos (optional)
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {attachments.length} / {MAX_ATTACHMENTS}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: attachments.length > 0 ? 1 : 0 }}>
                  {attachments.map((a) => (
                    <Box key={a.id} sx={{
                      position: 'relative', width: 72, height: 72, borderRadius: 1.5,
                      overflow: 'hidden', border: '1px solid',
                      borderColor: a.error ? 'error.main' : 'divider',
                      bgcolor: 'action.hover',
                    }}>
                      <Box
                        component="img"
                        src={a.previewUrl}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover',
                          opacity: a.url ? 1 : 0.7 }}
                      />
                      {!a.url && !a.error && (
                        <Box sx={{ position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0.24)' }}>
                          <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                        </Box>
                      )}
                      {a.error && (
                        <Box sx={{ position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(220,38,38,0.7)', color: '#FFFFFF',
                            fontSize: 9.5, fontWeight: 700, textAlign: 'center', px: 0.5 }}>
                          {a.error}
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removeAttachment(a.id)}
                        sx={{
                          position: 'absolute', top: 2, right: 2,
                          width: 20, height: 20,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                          color: '#FFFFFF', padding: 0,
                        }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}

                  {attachments.length < MAX_ATTACHMENTS && (
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />}
                      sx={{ height: 72, minWidth: 96, borderRadius: 1.5,
                        borderStyle: 'dashed', textTransform: 'none',
                        fontSize: 12, fontWeight: 600, color: 'text.secondary',
                        borderColor: 'divider' }}
                    >
                      Add photo
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) => {
                          handleFilesPicked(e.target.files)
                          // Reset so re-picking the same file still fires.
                          e.target.value = ''
                        }}
                      />
                    </Button>
                  )}
                </Stack>

                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
                  JPG, PNG, WebP or HEIC · up to 5 MB each
                </Typography>
              </Box>

              {submitErr && (
                <Alert severity="error">{submitErr}</Alert>
              )}

              <Button
                type="submit" variant="contained" size="large" fullWidth
                disabled={submitting || anyUploading || !categoryKey || !name.trim() || !email.trim()}
                sx={{ py: 1.4, fontWeight: 700 }}
              >
                {submitting ? 'Submitting…' : anyUploading ? 'Waiting for photos…' : 'Submit report'}
              </Button>
            </Stack>
          </form>
        </Paper>

        <Typography sx={{ mt: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
          {boot.org.name} · Site {boot.site.name}
        </Typography>
      </Container>
    </Box>
  )
}
